import { useTranslation } from "react-i18next";
import { Badge, DefaultMantineColor, Flex, Text, Tooltip } from "@mantine/core";
import { CellContext } from "@tanstack/react-table";
import { CampaignApplicationResolutionStatus, CampaignApplicationState } from "src/graphql/generated/graphql";
import { CampaignApplicationRestApi } from "src/types";

/**
 * TTV2-1753: the tester left testing — declined participation ("Zrušit účast") or let the
 * confirmation deadline lapse. Neither touches latestResolution, which stays "selected", so
 * without this the row reads "Tester". Same rule as the admin SPA's isWithdrawnApplication.
 */
export const isWithdrawnApplication = (campaignApplication: CampaignApplicationRestApi): boolean =>
    campaignApplication?.isWithdrawn === true || campaignApplication?.participationConfirmation?.status === "declined";

/** The role word the grid prints for an application, or null where it prints "INVALID". */
export const campaignApplicationRoleLabel = (
    campaignApplication: CampaignApplicationRestApi,
    t: (key: string) => string
): string | null => {
    if (isWithdrawnApplication(campaignApplication)) {
        return t("campaignapplication_role_withdrawn");
    }

    const isPending = campaignApplication?.state === CampaignApplicationState.Pending;

    if (!isPending && campaignApplication?.state !== CampaignApplicationState.Resolved) {
        return t("campaignapplication_role_none");
    }

    const status = isPending
        ? campaignApplication?.pendingResolution?.status
        : campaignApplication?.latestResolution?.status;

    switch (status) {
        case CampaignApplicationResolutionStatus.Selected:
            return t("campaignapplication_role_selected");
        case CampaignApplicationResolutionStatus.Alternate:
            return t("campaignapplication_role_alternate");
        case CampaignApplicationResolutionStatus.Rejected:
            return campaignApplication?.isUnselected
                ? t("campaignapplication_role_unselected")
                : t("campaignapplication_role_rejected");
        default:
            return null;
    }
};

export interface CellRoleProps extends CellContext<CampaignApplicationRestApi, unknown> {}
export const CellRole = ({ row }: CellRoleProps) => {
    const { t } = useTranslation(["common"]);

    const text: string = campaignApplicationRoleLabel(row?.original, (key) => t(key)) ?? "INVALID";

    if (isWithdrawnApplication(row?.original)) {
        return (
            <Flex display="inline-flex" align="center">
                <Text color="red">{text}</Text>
            </Flex>
        );
    }

    let color: DefaultMantineColor;
    let badgeText: string;

    switch (row?.original?.state) {
        case CampaignApplicationState.Pending:
            badgeText = t("campaignapplication_role_pending");

            switch (row?.original?.pendingResolution?.status) {
                case CampaignApplicationResolutionStatus.Selected:
                    color = "green";
                    break;
                case CampaignApplicationResolutionStatus.Alternate:
                    color = "yellow";
                    break;
                case CampaignApplicationResolutionStatus.Rejected:
                    color = "red";
                    break;
                default:
                    badgeText = undefined;
                    break;
            }
            break;
        case CampaignApplicationState.Resolved:
            switch (row?.original?.latestResolution?.status) {
                case CampaignApplicationResolutionStatus.Selected:
                    color = "green";
                    break;
                case CampaignApplicationResolutionStatus.Alternate:
                    color = "yellow";
                    break;
                case CampaignApplicationResolutionStatus.Rejected:
                    color = "red";
                    break;
            }
    }

    const textNode = <Text color={color}>{text}</Text>;

    return (
        <Flex display="inline-flex" align="center">
            {textNode}
            {badgeText && (
                <Tooltip label={t("campaignapplication_role_pending_description")} withArrow>
                    <Badge size="xs" variant="dot" color="grape" mb="xs" ml="4px">
                        {badgeText}
                    </Badge>
                </Tooltip>
            )}
        </Flex>
    );
};
